import { selectCurrentUser } from '@saniplus/api-client';
import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router';

import { PHOTO_PROMPT_PATH } from '../../app/config';
import { useAppSelector } from '../../app/hooks';
import { selectPhotoPromptDismissed } from './photoPromptSlice';

/**
 * P-01 — the first-login photo offer, as a route layer rather than a modal on
 * the punch screen.
 *
 * App-local on purpose, where `RequireAuth` and `RequirePinChanged` are shared
 * (`packages/ui/src/routing/guards.tsx`): this is the *only* app that prompts,
 * and the only role that is prompted is `worker`. A supervisor or an admin who
 * signs into the PWA goes straight to the shell — their photo is managed from
 * the admin portal (P-06).
 *
 * It is not a security control. Nothing behind it is protected; the worker can
 * leave with one tap, and the API neither knows nor cares whether the offer
 * was shown.
 */
export function RequirePhotoPrompted({ children }: { children?: ReactNode }) {
  const user = useAppSelector(selectCurrentUser);
  const dismissed = useAppSelector(selectPhotoPromptDismissed);

  const owed = user?.role === 'worker' && user.photoUpdatedAt === null && !dismissed;
  if (owed) return <Navigate to={PHOTO_PROMPT_PATH} replace />;

  return <>{children ?? <Outlet />}</>;
}
